import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BankFormValues } from '.';
import { type BankDetailsDataFragment } from '../../../../api/fragments/BankDetailsData';
import { BankIntegrationProvider, ModuleRole } from '../../../../api/types';
import ColorPickerField from '../../../../components/fields/ColorPickerField';
import DealershipPriceDisclaimerField from '../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import TextAreaField from '../../../../components/fields/TextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type BankDetailsFormProps = {
    updated: string;
    bank: BankDetailsDataFragment;
    extra: JSX.Element;
    offset: string;
    disabled?: boolean;
};

const EditBankDetailsForm = ({ bank, extra, updated, offset, disabled = false }: BankDetailsFormProps) => {
    const { t } = useTranslation(['bankDetails', 'common', 'financeProductDetails']);

    const { yesNoSwitch } = useSystemSwitchData();
    const markdownInfoTooltip = useMarkDownInfoTooltip();

    const { values, setFieldValue } = useFormikContext<BankFormValues>();
    useEffect(() => {
        if (!values.sendEmailToCustomer) {
            setFieldValue('sendPDFToCustomer', false);
        }
    }, [setFieldValue, values.sendEmailToCustomer]);

    const showCommentsField = useMemo(
        () =>
            [BankIntegrationProvider.Email, BankIntegrationProvider.Hlf, BankIntegrationProvider.Hlfv2].some(
                integration => integration === bank?.integration?.provider
            ),
        [bank?.integration?.provider]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={t('bankDetails:cardTitles.bankDetails')}>
            <Panel
                key={t('bankDetails:cardTitles.bankDetails')}
                extra={extra}
                header={t('bankDetails:cardTitles.bankDetails')}
            >
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('bankDetails:fields.name', { returnObjects: true })}
                            disabled={disabled}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.TranslatedInputField
                            {...t('bankDetails:fields.legalName', { returnObjects: true })}
                            disabled={disabled}
                            name="legalName"
                            required
                        />
                    </Col>

                    <Col {...colSpan}>
                        <FormFields.DisplayField
                            {...t('bankDetails:fields.module', { returnObjects: true })}
                            value={bank.module.displayName}
                        />
                    </Col>

                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('bankDetails:fields.email', { returnObjects: true })}
                            disabled={disabled}
                            name="email"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ColorPickerField
                            {...t('bankDetails:fields.color', { returnObjects: true })}
                            disabled={disabled}
                            name="color"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SingleUploadField
                            {...t('bankDetails:fields.logo', { returnObjects: true })}
                            disabled={disabled}
                            extensions={allowedExtensions.image}
                            name="logo"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SingleUploadField
                            {...t('bankDetails:fields.font', { returnObjects: true })}
                            disabled={disabled}
                            extensions={allowedExtensions.font}
                            name="font"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('bankDetails:fields.orderNo', { returnObjects: true })}
                            disabled={disabled}
                            name="order"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <TextAreaField
                            {...t(`bankDetails:fields.remoteFlowAcknowledgmentInfo`, { returnObjects: true })}
                            disabled={disabled}
                            name="remoteFlowAcknowledgmentInfo"
                            required
                        />
                    </Col>
                    {bank.module.company.countryCode === 'NZ' && (
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('bankDetails:fields.estFee', { returnObjects: true })}
                                disabled={disabled}
                                name="estFee"
                                required
                            />
                        </Col>
                    )}
                    <Col lg={8} xs={24}>
                        <DealershipPriceDisclaimerField
                            {...t('bankDetails:fields.financingDisclaimer', { returnObjects: true })}
                            disabled={disabled}
                            disclaimerKind="financingDisclaimer"
                            name="financingDisclaimer"
                            tooltip={markdownInfoTooltip}
                            isMultipleEntry
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ModuleSelectField
                            {...t(`bankDetails:fields.submissionApproval`, { returnObjects: true })}
                            companyId={bank.module.companyId}
                            disabled={disabled}
                            moduleRole={ModuleRole.Signing}
                            name="submissionApprovalModuleId"
                            addNoneOption
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ModuleSelectField
                            {...t(`bankDetails:fields.reSubmissionApproval`, { returnObjects: true })}
                            companyId={bank.module.companyId}
                            disabled={disabled}
                            moduleRole={ModuleRole.Signing}
                            name="reSubmissionApprovalModuleId"
                            addNoneOption
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('bankDetails:fields.hasVSOUpload', { returnObjects: true })}
                            disabled={disabled}
                            name="hasVSOUpload"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('bankDetails:fields.hasUploadDocuments', { returnObjects: true })}
                            disabled={disabled}
                            name="hasUploadDocuments"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('bankDetails:fields.sendEmailToCustomer', { returnObjects: true })}
                            disabled={disabled}
                            name="sendEmailToCustomer"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    {values.sendEmailToCustomer && (
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('bankDetails:fields.sendPDFToCustomer', { returnObjects: true })}
                                disabled={disabled}
                                name="sendPDFToCustomer"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                    )}

                    {showCommentsField && (
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('bankDetails:fields.showCommentsField', { returnObjects: true })}
                                disabled={disabled}
                                name="showCommentsField"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                    )}

                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('bankDetails:fields.allowVariantCodeMapping', { returnObjects: true })}
                            disabled={disabled}
                            name="allowVariantCodeMapping"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>

                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('bankDetails:fields.active', { returnObjects: true })}
                            disabled={disabled}
                            name="isActive"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.DisplayFieldWithUTCOffset
                            {...t('common:fields.updated', { returnObjects: true, offset })}
                            value={updated}
                        />
                    </Col>
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default EditBankDetailsForm;
